import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWebRTC } from '../context/WebRTCContext';
import { useFeature } from '../hooks/useFeatures';
import { getEventMeta } from '../constants/eventLabels';
import { hasRecentActionForFeature } from './ActionAuditLog';
import './ToastNotifications.css';

/**
 * Timeline event types that are the echo-back of an action the operator can
 * trigger directly from the UI, mapped to the desktop featureId that action
 * uses. When the operator just fired one of these themselves, they've already
 * seen the confirmation flash + it's in the audit log, so the timeline toast is
 * a redundant third confirmation — we drop it. The SAME event arriving without a
 * recent operator action (AI- or schedule-driven) still toasts.
 */
const OPERATOR_ECHO_FEATURE = {
    'punishment.shock_delivered': 'pishock',
    'punishment.lockout_started': 'time-limit',
    'punishment.input_sabotaged': 'input-sabotage',
    'limit.app_blocked': 'time-limit',
    'limit.threshold_hit': 'time-limit',
    'wheel.outcome_delivered': 'spin-wheel',
};

/**
 * Event types that should trigger toast notifications, grouped by priority.
 * Maps event type → { priority, route, duration }
 */
const TOAST_EVENT_CONFIG = {
    // Critical — attention-demanding, longer display
    'activity.keyword_flagged': { priority: 'critical', route: '/app/monitoring/sacred-timeline', duration: 6000 },
    'activity.media_flagged': { priority: 'critical', route: '/app/monitoring/sacred-timeline', duration: 6000 },
    'punishment.lockout_started': { priority: 'critical', route: '/app/monitoring/time-limit', duration: 6000 },
    'interrogation.verdict_rendered': { priority: 'critical', route: '/app/communication/interrogation', duration: 6000 },

    // Warning — important state changes
    'limit.threshold_hit': { priority: 'warning', route: '/app/monitoring/time-limit', duration: 5000 },
    'limit.app_blocked': { priority: 'warning', route: '/app/monitoring/time-limit', duration: 5000 },
    'punishment.shock_delivered': { priority: 'warning', route: '/app/integrations/pishock', duration: 5000 },
    'punishment.input_sabotaged': { priority: 'warning', route: '/app/punishment/input-sabotage', duration: 4000 },
    'journal.entry_rejected': { priority: 'warning', route: '/app/monitoring/sacred-timeline', duration: 5000 },
    'journal.rewrite_demanded': { priority: 'warning', route: '/app/monitoring/sacred-timeline', duration: 5000 },

    // Informational — auto-dismiss quickly
    'chat.message_received': { priority: 'info', route: '/app/communication/chat', duration: 4000 },
    'journal.entry_submitted': { priority: 'info', route: '/app/monitoring/sacred-timeline', duration: 4000 },
    'challenge.completed': { priority: 'success', route: '/app/punishment/challenges', duration: 4000 },
    'challenge.failed': { priority: 'warning', route: '/app/punishment/challenges', duration: 5000 },
    'punishment.write_lines_completed': { priority: 'success', route: '/app/punishment/write-lines', duration: 4000 },
    'wheel.outcome_delivered': { priority: 'info', route: '/app/punishment/spin-wheel', duration: 5000 },
    'economy.penalty_applied': { priority: 'warning', route: '/app/punishment/economy', duration: 4000 },
    'ai.suggestion_generated': { priority: 'info', route: '/app/monitoring/sacred-timeline', duration: 5000 },
    'ai.secret_discovered': { priority: 'info', route: '/app/monitoring/dossier', duration: 5000 },
    'system.disconnected': { priority: 'critical', route: null, duration: 6000 },
};

const MAX_TOASTS = 3;

let toastIdCounter = 0;

/**
 * Imperative toast API — the ergonomic "that failed" (or "that worked") channel
 * views were missing, which is why ~23 of them logged errors to the console
 * with no user-facing surface. Call from anywhere:
 *
 *   import { toastError } from '.../ToastNotifications';
 *   try { ... } catch (e) { toastError('Could not load the journal entry.'); }
 *
 * Delivered via a module-level pub/sub the mounted <ToastNotifications> listens
 * to; toasts fired before mount are queued and flushed on subscribe.
 */
const MANUAL_TYPE_META = {
    error:   { priority: 'critical', icon: '⚠', color: '#dc2626', duration: 6000 },
    warning: { priority: 'warning',  icon: '⚠', color: '#d97706', duration: 5000 },
    success: { priority: 'success',  icon: '✓', color: '#16a34a', duration: 4000 },
    info:    { priority: 'info',     icon: 'ℹ', color: '#9f8cc7', duration: 4000 },
};

const manualToastListeners = new Set();
let pendingManualToasts = [];

export function toast(message, opts = {}) {
    const detail = {
        message: typeof message === 'string' ? message : String(message ?? ''),
        type: MANUAL_TYPE_META[opts.type] ? opts.type : 'info',
        title: opts.title,
        duration: opts.duration,
        route: opts.route || null,
    };
    if (manualToastListeners.size === 0) {
        pendingManualToasts.push(detail);
    } else {
        manualToastListeners.forEach(fn => fn(detail));
    }
    return detail;
}

export const toastError = (message, opts) => toast(message, { ...opts, type: 'error' });
export const toastWarning = (message, opts) => toast(message, { ...opts, type: 'warning' });
export const toastSuccess = (message, opts) => toast(message, { ...opts, type: 'success' });
export const toastInfo = (message, opts) => toast(message, { ...opts, type: 'info' });

const ToastNotifications = () => {
    const navigate = useNavigate();
    const { isConnected } = useWebRTC();
    // useFeature polls for sacred-timeline registration — replaces a bare
    // featureManager.getFeature call that could race with cold-load mount.
    const sacredFeature = useFeature('sacred-timeline');
    const [toasts, setToasts] = useState([]);
    const timersRef = useRef(new Map());
    // Mirror of `toasts` for synchronous reads in addToast (coalescing decision)
    // without adding toasts to the callback's deps.
    const toastsRef = useRef([]);
    useEffect(() => { toastsRef.current = toasts; }, [toasts]);

    // Dismiss a toast (with exit animation)
    const dismissToast = useCallback((id) => {
        setToasts(prev => prev.map(t =>
            t.id === id ? { ...t, exiting: true } : t
        ));
        // Remove after animation
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 260);
        // Clear auto-dismiss timer
        const timer = timersRef.current.get(id);
        if (timer) {
            clearTimeout(timer);
            timersRef.current.delete(id);
        }
    }, []);

    // Commit a fully-built toast: coalesce a matching head, else enqueue with
    // eviction + an auto-dismiss timer. Shared by the event path and the
    // imperative toast() path so both behave identically.
    const commitToast = useCallback((toast) => {
        const { type, title, body, duration } = toast;
        const head = toastsRef.current[0];
        if (head && !head.exiting && head.type === type && head.title === title) {
            setToasts(prev => prev.map((t, i) =>
                i === 0 ? { ...t, count: (t.count || 1) + 1, body: body || t.body, timestamp: toast.timestamp } : t
            ));
            const oldTimer = timersRef.current.get(head.id);
            if (oldTimer) clearTimeout(oldTimer);
            timersRef.current.set(head.id, setTimeout(() => dismissToast(head.id), duration));
            return;
        }

        setToasts(prev => {
            const next = [toast, ...prev];
            if (next.length > MAX_TOASTS) {
                const evicted = next.slice(MAX_TOASTS);
                evicted.forEach(t => {
                    const timer = timersRef.current.get(t.id);
                    if (timer) clearTimeout(timer);
                    timersRef.current.delete(t.id);
                });
                return next.slice(0, MAX_TOASTS);
            }
            return next;
        });

        const timer = setTimeout(() => dismissToast(toast.id), duration);
        timersRef.current.set(toast.id, timer);
    }, [dismissToast]);

    // Add a toast from a Sacred Timeline event
    const addToast = useCallback((event) => {
        const toastConfig = TOAST_EVENT_CONFIG[event.type];
        if (!toastConfig) return;

        // Drop the redundant third confirmation for an action the operator just
        // triggered themselves (already flashed + logged in the audit log).
        const echoFeature = OPERATOR_ECHO_FEATURE[event.type];
        if (echoFeature && hasRecentActionForFeature(echoFeature)) return;

        const eventConfig = getEventMeta(event.type);

        // For keyword flags, surface the matched keyword(s) in the body so the
        // user can immediately see *why* it fired (e.g. "cage" matched in HN
        // page content) without having to open the detail panel.
        let body = event.summary || event.data?.processName || event.data?.url || event.data?.reason || '';
        if (event.type === 'activity.keyword_flagged') {
            const kws = event.data?.matchedKeywords && event.data.matchedKeywords.length
                ? event.data.matchedKeywords
                : (event.data?.flaggedGroups || []).map(g => g.keyword).filter(Boolean);
            const pageTitle = event.data?.title || event.data?.url || '';
            if (kws.length > 0) {
                body = `matched "${kws.join(', ')}"${pageTitle ? ` in ${pageTitle}` : ''}`;
            }
        }

        commitToast({
            id: ++toastIdCounter,
            type: event.type,
            title: event.title || eventConfig.label,
            body,
            icon: eventConfig.icon,
            color: eventConfig.color,
            priority: toastConfig.priority,
            route: toastConfig.route,
            eventId: event.id || null,
            timestamp: event.timestamp || Date.now(),
            duration: toastConfig.duration,
            count: 1,
            exiting: false,
        });
    }, [commitToast]);

    // Subscribe to the imperative toast() API (module-level pub/sub).
    useEffect(() => {
        const handleManual = (detail) => {
            const meta = MANUAL_TYPE_META[detail.type] || MANUAL_TYPE_META.info;
            // With no explicit title, the message is the headline (no body).
            const title = detail.title || detail.message;
            const body = detail.title ? detail.message : '';
            commitToast({
                id: ++toastIdCounter,
                type: `manual.${detail.type}`,
                title,
                body,
                icon: meta.icon,
                color: meta.color,
                priority: meta.priority,
                route: detail.route,
                eventId: null,
                timestamp: Date.now(),
                duration: detail.duration || meta.duration,
                count: 1,
                exiting: false,
            });
        };
        manualToastListeners.add(handleManual);
        // Flush any toasts fired before this component mounted.
        if (pendingManualToasts.length > 0) {
            const queued = pendingManualToasts;
            pendingManualToasts = [];
            queued.forEach(handleManual);
        }
        return () => { manualToastListeners.delete(handleManual); };
    }, [commitToast]);

    // Subscribe to Sacred Timeline events
    useEffect(() => {
        if (!isConnected || !sacredFeature) return undefined;

        const handleTimelineEvent = (event) => {
            if (TOAST_EVENT_CONFIG[event.type]) {
                addToast(event);
            }
        };

        const unsubLive = sacredFeature.on('sacred_event', (eventData) => {
            const event = eventData.payload || eventData;
            if (event && event.type) handleTimelineEvent(event);
        });

        return () => unsubLive();
    }, [sacredFeature, isConnected, addToast]);

    // Cleanup timers on unmount
    useEffect(() => {
        return () => {
            timersRef.current.forEach(timer => clearTimeout(timer));
            timersRef.current.clear();
        };
    }, []);

    const handleClick = useCallback((toast) => {
        if (toast.route) {
            // Preserve the event id so the destination view can deep-link to
            // the relevant detail panel (e.g. Sacred Timeline opens the
            // event's slide-out so the user can see context + take action).
            const url = toast.eventId
                ? `${toast.route}?eventId=${encodeURIComponent(toast.eventId)}`
                : toast.route;
            navigate(url);
        }
        dismissToast(toast.id);
    }, [navigate, dismissToast]);

    if (toasts.length === 0) return null;

    return (
        // aria-live so screen readers announce toasts as they arrive — these are
        // surveillance/enforcement alerts (a flag fired, a shock landed), so
        // "assertive". Previously the app had zero live regions.
        <div className="toast-container" role="region" aria-label="Notifications" aria-live="assertive" aria-atomic="false">
            {toasts.map(toast => (
                <div
                    key={toast.id}
                    className={`toast-item toast-${toast.priority} ${toast.exiting ? 'toast-exiting' : ''}`}
                    style={{ '--toast-color': toast.color, '--toast-duration': `${toast.duration}ms` }}
                    onClick={() => handleClick(toast)}
                >
                    <div className="toast-icon">
                        <span>{toast.icon}</span>
                    </div>
                    <div className="toast-content">
                        <span className="toast-title">
                            {toast.title}
                            {toast.count > 1 && <span className="toast-count">×{toast.count}</span>}
                        </span>
                        {toast.body && <span className="toast-body">{toast.body}</span>}
                    </div>
                    <button
                        className="toast-close"
                        onClick={(e) => { e.stopPropagation(); dismissToast(toast.id); }}
                    >
                        &times;
                    </button>
                    <div className="toast-progress" />
                </div>
            ))}
        </div>
    );
};

export default ToastNotifications;
